import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  useTheme,
  TextField,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
} from "@mui/material";
import { Search, Clear } from "@mui/icons-material";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import { getBookings, getBookingById } from "service/admin/bookingService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";

const statusChip = (status) => {
  const map = {
    confirmed: { label: "Confirmed", color: "success" },
    pending: { label: "Pending", color: "warning" },
    cancelled: { label: "Cancelled", color: "error" },
    completed: { label: "Completed", color: "info" },
    failed: { label: "Failed", color: "error" },
    paid: { label: "Paid", color: "success" },
    refunded: { label: "Refunded", color: "info" },
  };
  const cfg = map[status] || { label: status || "-", color: "default" };
  return <Chip label={cfg.label} size="small" color={cfg.color} />;
};

const BookingDetailDialog = ({ bookingId, open, onClose }) => {
  const theme = useTheme();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && bookingId) {
      setLoading(true);
      getBookingById(bookingId)
        .then((r) => setBooking(r.data))
        .catch(() => toast.error("Failed to load booking details."))
        .finally(() => setLoading(false));
    }
  }, [open, bookingId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Booking Detail</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : booking ? (
          <Grid container spacing={1}>
            {[
              ["Booking Ref", booking.booking_ref],
              ["Athlete", booking.athlete?.full_name],
              ["Facility", booking.facility?.name],
              ["Activity", booking.activity?.name],
              ["Date", booking.booking_date ? moment(booking.booking_date).format("DD MMM YYYY") : "-"],
              ["Slot", booking.slot_time],
              ["Amount", booking.amount ? `₹${booking.amount}` : "-"],
              ["Status", booking.status],
              ["Payment Status", booking.payment_status],
              ["Created At", booking.created_at ? moment(booking.created_at).format("DD MMM YYYY HH:mm") : "-"],
            ].map(([label, value]) => (
              <React.Fragment key={label}>
                <Grid item xs={5}>
                  <Typography variant="body2" color={theme.palette.secondary[300]}>
                    {label}
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" color={theme.palette.secondary[100]}>
                    {value || "-"}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const Bookings = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: page + 1,
        limit: pageSize,
        search: search || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        payment_status: paymentStatusFilter !== "all" ? paymentStatusFilter : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      const response = await getBookings(params);
      const data = response.data.data;
      setRows(Array.isArray(data) ? data : data.data || []);
      if (data.pagination?.total !== undefined) setRowCount(data.pagination.total);
    } catch (error) {
      toast.error("Failed to load bookings.");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, search, statusFilter, paymentStatusFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(0);
  };

  const columns = [
    {
      field: "booking_ref",
      headerName: "BOOKING REF",
      flex: 1,
    },
    {
      field: "athlete_name",
      headerName: "ATHLETE",
      flex: 1,
      renderCell: (params) => params.row.athlete?.full_name || params.value || "-",
    },
    {
      field: "facility_name",
      headerName: "FACILITY",
      flex: 1.2,
      renderCell: (params) => params.row.facility?.name || params.value || "-",
    },
    {
      field: "booking_date",
      headerName: "DATE",
      flex: 0.9,
      renderCell: (params) =>
        params.value ? moment(params.value).format("DD MMM YYYY") : "-",
    },
    {
      field: "amount",
      headerName: "AMOUNT",
      flex: 0.7,
      renderCell: (params) => (params.value ? `₹${params.value}` : "-"),
    },
    {
      field: "status",
      headerName: "STATUS",
      flex: 0.9,
      renderCell: (params) => statusChip(params.value),
    },
    {
      field: "payment_status",
      headerName: "PAYMENT",
      flex: 0.9,
      renderCell: (params) => statusChip(params.value),
    },
    {
      field: "created_at",
      headerName: "CREATED",
      flex: 0.9,
      renderCell: (params) =>
        params.value ? moment(params.value).format("DD MMM YYYY") : "-",
    },
  ];

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <FlexBetween mb={1}>
          <Header title="BOOKINGS" subTitle="All booking records" />
        </FlexBetween>

        <Box mt="1rem" mb="0.5rem" display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search bookings..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            color="secondary"
            sx={{ width: 240 }}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton onClick={handleSearch} color="secondary">
            <Search />
          </IconButton>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel color="secondary">Status</InputLabel>
            <Select
              color="secondary"
              label="Status"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel color="secondary">Payment Status</InputLabel>
            <Select
              color="secondary"
              label="Payment Status"
              value={paymentStatusFilter}
              onChange={(e) => { setPaymentStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="From"
            type="date"
            color="secondary"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            color="secondary"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
        </Box>

        <Box
          mt="10px"
          height="65vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <DataGrid
            loading={isLoading}
            getRowId={(row) => row.id}
            rows={rows}
            columns={columns}
            rowCount={rowCount || rows.length}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            page={page}
            pageSize={pageSize}
            paginationMode={rowCount > 0 ? "server" : "client"}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => { setPageSize(newSize); setPage(0); }}
            onRowClick={(params) => {
              setSelectedBookingId(params.row.id);
              setDetailOpen(true);
            }}
            components={{ Toolbar: DataGridCustomToolbar }}
            sx={{
              cursor: "pointer",
              "& .MuiCircularProgress-circle": { color: theme.palette.secondary[300] },
            }}
          />
        </Box>
      </Box>

      <BookingDetailDialog
        open={detailOpen}
        bookingId={selectedBookingId}
        onClose={() => setDetailOpen(false)}
      />
    </>
  );
};

export default Bookings;
